import { useCallback, useState } from "react";
import { useAuth0 } from "../react-auth0-spa";
import { useAppContext } from "../context";
import config from "../api_config.json";

const parseResponse = async (response) => {
  let payload = {};
  try {
    payload = await response.json();
  } catch (_error) {
    // Keep the HTTP status as the useful error when the body is not JSON.
  }

  if (!response.ok) {
    const error = new Error(payload.error || `Errore HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return payload;
};

export const useAuth0UserBlocks = () => {
  const { getTokenSilently, user: authenticatedUser } = useAuth0();
  const { isProd } = useAppContext();
  const [checkingUserIds, setCheckingUserIds] = useState(new Set());
  const [unlockingUserIds, setUnlockingUserIds] = useState(new Set());
  const canManageAuth0Blocks = Boolean(authenticatedUser);
  const apiBaseUrl = config.baseUrl[isProd ? "prod" : "dev"];

  const requestUserBlocks = useCallback(
    async (userId, method) => {
      const token = await getTokenSilently();
      const response = await fetch(
        `${apiBaseUrl}/users/${encodeURIComponent(userId)}/auth0-blocks`,
        {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return parseResponse(response);
    },
    [apiBaseUrl, getTokenSilently]
  );

  const checkUser = useCallback(
    async (userId) => {
      setCheckingUserIds((previous) => new Set(previous).add(userId));

      try {
        return await requestUserBlocks(userId, "GET");
      } finally {
        setCheckingUserIds((previous) => {
          const next = new Set(previous);
          next.delete(userId);
          return next;
        });
      }
    },
    [requestUserBlocks]
  );

  const unblockUser = useCallback(
    async (userId) => {
      setUnlockingUserIds((previous) => new Set(previous).add(userId));

      try {
        return await requestUserBlocks(userId, "DELETE");
      } finally {
        setUnlockingUserIds((previous) => {
          const next = new Set(previous);
          next.delete(userId);
          return next;
        });
      }
    },
    [requestUserBlocks]
  );

  return {
    canManageAuth0Blocks,
    checkUser,
    checkingUserIds,
    unblockUser,
    unlockingUserIds,
  };
};

export { parseResponse };
